import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import Error from '../layout/Error';
import { msalInstanceReady, tokenRequest, getTokenRequest } from '../../store/authConfig';
import { AuthenticationResult } from '@azure/msal-browser/dist/response/AuthenticationResult';

const printBtn = {
    width: '140px',
    fontSize: '13px',
    padding: '5px 10px',
    borderRadius: '9999px',
    textTransform: 'uppercase' as 'uppercase'
};

const PrintDropDown = (props: any) => {
    const [dropdownOpen, setOpen] = useState(false);
    const [percentage, setPercentage] = useState(0);
    const [isExporting, setIsExporting] = useState(false);
    const [majorError, setMajorError] = useState(false);

    const { reportGroup } = useParams<any>();
    const location = useLocation();

    const [currentReportGroup, setCurrentReportGroup] = useState('');

    useEffect(() => {
        const currentPath = location.pathname;
        const match = currentPath.match(/^\/([^\/]+)/);
        if (match) {
            setCurrentReportGroup(match[1]);
        }
    }, []);

    const toggle = () => setOpen(!dropdownOpen);

    const sendPrintRequest = useCallback(async (reportGroup: string, reportName: string) => {
        if (isExporting) return;
        // update state
        setIsExporting(true);
        msalInstanceReady.then(async (msalInstance) => { 
            let tokenResponse: AuthenticationResult | null = null;
            try {
                const account = msalInstance.getAllAccounts()[0];
                tokenResponse = await msalInstance.acquireTokenSilent(getTokenRequest(account));
            } catch {
                msalInstance.acquireTokenRedirect(tokenRequest);
            }
            var isMobile = window.innerWidth <= 576;
            await fetch(`PowerBI/Export?reportName=${reportName}&reportGroup=${reportGroup}&isMobile=${isMobile}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + tokenResponse?.accessToken
                    }
                })
                .then(response => {
                    if (response.ok) {
                        let intervalId = setInterval(async () => {
                            await fetch(`PowerBI/Poll?reportGroup=${reportGroup}&isMobile=${isMobile}`, {
                                    method: 'GET',
                                    headers: {
                                        'Accept': 'application/json',
                                        'Content-Type': 'application/json',
                                        'Authorization': 'Bearer ' + tokenResponse?.accessToken
                                    }
                                })
                                .then(response => response.json() as any)
                                .then(data => {
                                    setPercentage(data);
                                    if (data === 100) {
                                        window.location.href = (`/PowerBI/Download?reportGroup=${reportGroup}&isMobile=${isMobile}`);
                                        clearInterval(intervalId);
                                        setIsExporting(false);
                                        setPercentage(0);
                                    }
                                }).catch(error => {
                                    setMajorError(true);
                                    setIsExporting(false);
                                });
                        }, 10000);
                    }
                    else {
                        setMajorError(true);
                        setIsExporting(false);
                    }
                }).catch(error => {
                    setMajorError(true);
                    setIsExporting(false);
                });
        });
    }, [isExporting]);

    const sendExcelRequest = useCallback(async (reportGroup: string) => {
        if (isExporting) return;
        // update state
        setIsExporting(true);
        msalInstanceReady.then(async (msalInstance) => { 
            let tokenResponse: AuthenticationResult | null = null;
            try {
                const account = msalInstance.getAllAccounts()[0];
                tokenResponse = await msalInstance.acquireTokenSilent(getTokenRequest(account));
            } catch {
                msalInstance.acquireTokenRedirect(tokenRequest);
            }
            var isMobile = window.innerWidth <= 576;
            await fetch(`PowerBI/ExportExcelReport?reportGroup=${reportGroup}&isMobile=${isMobile}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + tokenResponse?.accessToken
                    }
                })
                .then(response => {
                    if (response.ok) {
                        let intervalId = setInterval(async () => {
                            await fetch(`PowerBI/PollExcelReport?reportGroup=${reportGroup}&isMobile=${isMobile}`, {
                                    method: 'GET',
                                    headers: {
                                        'Accept': 'application/json',
                                        'Content-Type': 'application/json',
                                        'Authorization': 'Bearer ' + tokenResponse?.accessToken
                                    }
                                })
                                .then(response => response.json() as any)
                                .then(data => {
                                    if (data === 101) {
                                        setPercentage(0);
                                        setMajorError(true);
                                        setIsExporting(false);
                                        clearInterval(intervalId);
                                    }
                                    else if (data === 100) {
                                        window.location.href = (`/PowerBI/DownloadExcelReport?reportGroup=${reportGroup}&isMobile=${isMobile}`);
                                        clearInterval(intervalId);
                                        setIsExporting(false);
                                        setPercentage(0);
                                    }
                                    else {
                                        setPercentage(data);
                                    }
                                }).catch(error => {
                                    setMajorError(true);
                                    setIsExporting(false);
                                });
                        }, 10000);
                    }
                    else {
                        setMajorError(true);
                        setIsExporting(false);
                    }
                }).catch(error => {
                    setMajorError(true);
                    setIsExporting(false);
                });
        });
    }, [isExporting]);

    const menuItems = props.printMenuItems?.filter((p: any) => p.reportGroup === currentReportGroup) || [];

    return (
        <>
            <Error show={majorError} errorMsg={undefined} />
            {
                menuItems.length > 0 && window.innerWidth > 576 &&
                <li>
                    <ButtonDropdown isOpen={dropdownOpen} toggle={toggle} disabled={props.disableDropdown} direction="down">
                        <DropdownToggle caret={!isExporting} disabled={isExporting} style={printBtn}>
                            {isExporting ?
                                <>This may take a while... Percentage complete: {percentage}%</> :
                                <> <span className={props.disableDropdown ? "text-muted" : ""}>Print to PDF</span>  </>
                            }
                        </DropdownToggle>
                        <DropdownMenu style={{ maxWidth: '100px !important' }}>
                                {menuItems.map((p: any) => {
                                    if (p.isExcelReport)
                                        return <DropdownItem key={p.reportName} onClick={() => sendExcelRequest(currentReportGroup)}>{p.displayName}</DropdownItem>;
                                    else 
                                        return <DropdownItem key={p.reportName} onClick={() => sendPrintRequest(currentReportGroup, p.reportName)}>{p.displayName}</DropdownItem>;
                                })}
                        </DropdownMenu>
                    </ButtonDropdown>
                </li>
            }
        </>
    );
}

export default PrintDropDown;
